import React from "react";
import PropTypes from "prop-types";
import {
  Container,
  Grid,
  Typography,
  Tabs,
  Tab,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getLanguageCookie } from "../../../language/LanguageFunctions";
import Hero from "../../../components/layout/Hero";
import SubscriptionCard from "./SubscriptionCard";
import {
  getEnvironmentVariable,
  supportsUYPlatformFeatures
} from "../../../global/environmentVariables";

const SUPPORTS_UY_PLATFORM_FEATURES = supportsUYPlatformFeatures();

const OrganizationSubscriptionBlock = ({
  tabStates,
  setTabStates,
  user,
  userSubscriptions,
  globalVars,
  affiliate,
  props,
  SYSTEM_IMAGES_CLOUDFRONT,
  orgId
}) => {
  const theme = useTheme();
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.subscription"
  });

  const benefits = [
    {
      text: t("hostUnlimitedPublicEvents", {
        defaultValue: "Host unlimited public events"
      }),
      isAvailable: true
    },
    {
      text: t("embeddableCalendarAndLeaderboard", {
        defaultValue: "Embeddable calendar and leaderboard"
      }),
      isAvailable: true
    },
    {
      text: t("assignAndManageStaff", {
        defaultValue: "Assign and manage staff"
      }),
      isAvailable: true
    },
    {
      text: t("manageLocalRankings", { defaultValue: "Manage local rankings" }),
      isAvailable: true
    }
  ];

  if (!SUPPORTS_UY_PLATFORM_FEATURES) {
    // Insert after assign and manage staff
    benefits.splice(3, 0, {
      text: t("manageLocalCircuits", { defaultValue: "Manage local circuits" }),
      isAvailable: true
    });
  }

  return (
    <>
      <Hero
        backgroundImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/header.jpg`}
        fallbackImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/home1.png`}
        sxBackground={{
          height: ["50vh", "55vh"],
          maxHeight: ["250px", "350px"],
          minHeight: ["150px", "150px"],
          color: "common.white",
          width: "100%"
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.5rem", sm: "2rem" },
            textAlign: "center",
            mb: 1
          }}
        >
          {t("needMoreFeaturesForOrganization")}
        </Typography>
        {userSubscriptions?.organizationSubscription?.isSubscribed && (
          <Typography variant="caption">
            {t("youAreCurrentlySubscribed")}
          </Typography>
        )}
      </Hero>

      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          <Grid container justifyContent="center" sx={{ mb: 4 }}>
            <Tabs
              value={tabStates.organization}
              onChange={(event, newValue) =>
                setTabStates((prev) => ({
                  ...prev,
                  organization: newValue
                }))
              }
              aria-label="Organization subscription tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none"
                },
                "& .MuiTab-root": {
                  color: theme.palette.banner?.contrastText,
                  margin: "0 8px",
                  borderRadius: 1,
                  fontWeight: 700,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white"
                  }
                },
                backgroundColor: "transparent"
              }}
            >
              <Tab
                label={t("monthlyPricing")}
                value="Monthly"
                sx={{
                  minWidth: "150px",
                  borderRadius: 1,
                  fontWeight: 700
                }}
              />
              <Tab
                label={t("annualPricing")}
                value="Yearly"
                sx={{
                  minWidth: "150px",
                  borderRadius: 1,
                  fontWeight: 700
                }}
              />
            </Tabs>
          </Grid>

          <Grid
            container
            spacing={3}
            justifyContent="center"
            sx={{ px: { xs: 2, sm: 0 }, mb: 4 }}
          >
            {globalVars?.pricing?.products?.[
              SUPPORTS_UY_PLATFORM_FEATURES
                ? globalVars?.pricing?.subscriptions?.storeSubscription
                : globalVars?.pricing?.subscriptions?.circuitSuite
            ]?.prices
              ?.filter((price) => price.frequency === tabStates.organization)
              .map((price, index) => {
                // Calculate savings for annual organization pricing
                const monthlyPrice =
                  globalVars?.pricing?.products?.[
                    SUPPORTS_UY_PLATFORM_FEATURES
                      ? globalVars?.pricing?.subscriptions?.storeSubscription
                      : globalVars?.pricing?.subscriptions?.circuitSuite
                  ]?.prices?.find((p) => p.frequency === "Monthly")?.amount ||
                  0;
                const yearlyEquivalent = monthlyPrice * 12;
                const savings =
                  price.frequency === "Yearly" && yearlyEquivalent > 0
                    ? Math.round(yearlyEquivalent - price.amount)
                    : null;

                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <SubscriptionCard
                      title={t("tavernKeeperTitle")}
                      forWho={t("forOrganizations")}
                      price={price.amount}
                      currency={price.currency || "USD"}
                      interval={`${price.frequency === "Monthly" ? t("monthly") : t("yearly")} ${t("perOrganization")}`}
                      savingsAmount={savings}
                      benefits={benefits}
                      buttonText={
                        SUPPORTS_UY_PLATFORM_FEATURES
                          ? orgId
                            ? userSubscriptions.organizationSubscription
                                .isSubscribed
                              ? t("manageActivePlan")
                              : t("upgradeThisOrganization")
                            : t("selectAnOrganization")
                          : user &&
                              userSubscriptions.organizationSubscription
                                .isSubscribed
                            ? userSubscriptions.organizationSubscription
                                .type === price.frequency
                              ? t("activePlan")
                              : t("manageActivePlan")
                            : t("upgradeThisOrganization")
                      }
                      buttonLink={
                        SUPPORTS_UY_PLATFORM_FEATURES
                          ? orgId
                            ? userSubscriptions.organizationSubscription
                                .isSubscribed
                              ? `/subscription/organization/${userSubscriptions.organizationSubscription.id || ""}`
                              : `/subscription/organization/new?orgId=${orgId}&affiliate=${
                                  affiliate ||
                                  props?.affiliate ||
                                  (getEnvironmentVariable("APP_NAME")
                                    ? getEnvironmentVariable(
                                        "APP_NAME"
                                      ).toLowerCase()
                                    : "bcp")
                                }&priceId=${price.priceId}&productId=${
                                  globalVars?.pricing?.subscriptions
                                    ?.storeSubscription
                                }`
                            : `/organize/game-stores`
                          : user &&
                              userSubscriptions.organizationSubscription
                                .isSubscribed
                            ? "/account?active_tab=subscriptions"
                            : `/subscription/organization/new?affiliate=${
                                affiliate ||
                                props?.affiliate ||
                                (getEnvironmentVariable("APP_NAME")
                                  ? getEnvironmentVariable(
                                      "APP_NAME"
                                    ).toLowerCase()
                                  : "bcp")
                              }&priceId=${price.priceId}&productId=${
                                SUPPORTS_UY_PLATFORM_FEATURES
                                  ? globalVars?.pricing?.subscriptions
                                      ?.storeSubscription
                                  : globalVars?.pricing?.subscriptions
                                      ?.circuitSuite
                              }`
                      }
                      isDisabled={false}
                      sx={{ height: "100%", minHeight: "550px" }}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

OrganizationSubscriptionBlock.propTypes = {
  tabStates: PropTypes.shape({
    organization: PropTypes.string.isRequired
  }).isRequired,
  setTabStates: PropTypes.func.isRequired,
  user: PropTypes.object,
  userSubscriptions: PropTypes.shape({
    organizationSubscription: PropTypes.shape({
      isSubscribed: PropTypes.bool.isRequired,
      type: PropTypes.string
    }).isRequired
  }).isRequired,
  globalVars: PropTypes.object.isRequired,
  affiliate: PropTypes.string,
  props: PropTypes.object,
  SYSTEM_IMAGES_CLOUDFRONT: PropTypes.string.isRequired
};

export default OrganizationSubscriptionBlock;
